import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PicklistField } from "../blocks/fields/PicklistField";
import { Button } from "../elements/Button";
import { useDismissOnOutsideClick } from "../hooks/useDismissOnOutsideClick";
import { ChevronDown, ChevronUp, FilterIcon } from "../icons";
import "../lists/ListFilterBar.scss";
import { RuleBuilder } from "./RuleBuilder";
import { SearchFilterBarProps } from "./SearchFilterBar.types";

const ButtonClass =
  "btn border rounded-2 bg-white text-dark fw-medium hover-bg-light text-nowrap";

/**
 * The band above the results, laid out as /contacts lays out its filters: what to search
 * for, then Advanced filters (its rules open in a panel over the page), Clear and Search.
 */
export function SearchFilterBar({
  search,
  allowedEntityTypes,
  entityOptions,
  onEntityChange,
  submitText = "Submit",
  panelMaxHeight,
  onClear,
}: SearchFilterBarProps) {
  const { query, errors, loading } = search;
  const [open, setOpen] = useState(false);
  const bandRef = useRef<HTMLDivElement>(null);
  const hasErrors = Object.keys(errors).length > 0;
  const close = () => setOpen(false);

  useDismissOnOutsideClick(bandRef, open, close);

  // A rule that needs a value has to be on show to be filled in.
  useEffect(() => {
    if (hasErrors) {
      setOpen(true);
    }
  }, [hasErrors]);

  return (
    <div className="list-filter-bar py-2" ref={bandRef}>
      <div className="list-filter-bar__row">
        {entityOptions && (
          <div className="list-filter-bar__filter list-filter-bar__filter--wide">
            <PicklistField
              label="Search For"
              name="search-for"
              value={query.entityName}
              options={entityOptions}
              hidePlaceholder
              onChange={(value) => value && onEntityChange?.(value)}
            />
          </div>
        )}
        <div className={classNames("list-filter-bar__actions", !entityOptions && "pt-0")}>
          <button
            type="button"
            className={classNames(
              "list-filter-bar__advanced-toggle",
              ButtonClass,
              hasErrors && "border-danger text-danger"
            )}
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <FontAwesomeIcon icon={FilterIcon} className="me-2" />
            {query.nodes.length > 0
              ? `Advanced filters (${query.nodes.length})`
              : "Advanced filters"}
            <FontAwesomeIcon
              icon={open ? ChevronUp : ChevronDown}
              className="ms-2 d-none d-md-inline-block"
            />
          </button>
          {open && (
            <>
              <div className="list-filter-bar__advanced-backdrop d-md-none" onClick={close} />
              <div
                className="list-filter-bar__advanced"
                role="dialog"
                aria-label="Advanced filters"
                style={{
                  width: "min(60rem, 100%)",
                  maxWidth: "none",
                  ...(panelMaxHeight && { maxHeight: panelMaxHeight, overflowY: "auto" }),
                }}
              >
                <div className="d-flex d-md-none align-items-center justify-content-between mb-3">
                  <h3 className="modal-title">Advanced filters</h3>
                  <button type="button" className="btn btn-close" aria-label="Close" onClick={close} />
                </div>
                <RuleBuilder
                  plain
                  entityName={query.entityName}
                  allowedEntityTypes={allowedEntityTypes}
                  nodes={query.nodes}
                  errors={errors}
                  fieldsReady={search.fieldsReady}
                  onChange={search.setNodes}
                />
                <div className="d-md-none mt-3">
                  <Button text="Done" variant="primary" isBlock onClick={close} />
                </div>
              </div>
            </>
          )}
          <button type="button" className={ButtonClass} disabled={loading} onClick={onClear}>
            Clear
          </button>
          <Button
            text={submitText}
            className="entity-page__footer-btn--primary rounded-2 fw-medium"
            disabled={loading}
            showSpinner={loading}
            onClick={search.submit}
          />
        </div>
      </div>
    </div>
  );
}
